import { DateTime } from 'luxon'

import { Song } from '../song/song'

export class Playlist {
	public id: number
	public title: string
	public titleTranslations: Record<string, string>
	public description: string
	public descriptionTranslations: Record<string, string>
	public contentCategoryIds: number[]
	public public: boolean
	public cover: string
	public thumbs: string[]
	public hash: string
	public createdAt: DateTime
	public updatedAt: DateTime
	public feedbackDropRatio?: number
	public analytics?: PlaylistAnalytics

	// Relationships
	public songs?: Song[]
	public highlightedSongs?: Song[]

	// Pivot data
	public reviewed?: boolean | null
	public percentage?: number

	constructor(data: {
		id: number
		title: string
		titleTranslations: Record<string, string>
		description: string
		descriptionTranslations: Record<string, string>
		contentCategoryIds: number[]
		public: boolean
		cover: string
		thumbs: string[]
		hash: string
		createdAt: DateTime
		updatedAt: DateTime
		feedbackDropRatio?: number
		analytics?: PlaylistAnalytics
		songs?: Song[]
		highlightedSongs?: Song[]
		reviewed?: boolean | null
		percentage?: number
	}) {
		this.id = data.id
		this.title = data.title
		this.titleTranslations = data.titleTranslations
		this.description = data.description
		this.descriptionTranslations = data.descriptionTranslations
		this.contentCategoryIds = data.contentCategoryIds
		this.public = data.public
		this.cover = data.cover
		this.thumbs = data.thumbs
		this.hash = data.hash
		this.createdAt = data.createdAt
		this.updatedAt = data.updatedAt
		this.feedbackDropRatio = data.feedbackDropRatio
		this.analytics = data.analytics

		this.songs = data.songs
		this.highlightedSongs = data.highlightedSongs

		this.reviewed = data.reviewed
		this.percentage = data.percentage
	}

	static create(data: Partial<Playlist>): Playlist {
		const defaultData = {
			id: 0,
			title: '',
			titleTranslations: {},
			description: '',
			descriptionTranslations: {},
			contentCategoryIds: [],
			public: false,
			cover: '',
			thumbs: [],
			hash: '',
			createdAt: DateTime.now(),
			updatedAt: DateTime.now(),
			songs: undefined,
			highlightedSongs: undefined,
			analytics: PlaylistAnalytics.create({}),
			reviewed: undefined,
			percentage: undefined,
		}

		return new Playlist({
			...defaultData,
			...data,
		})
	}

	clone(): Playlist {
		return new Playlist({
			...this,
		})
	}

	/**
	 * Get the smallest thumbnail image.
	 * Returns the image with "small" in the filename, or the cover if no thumbs exist.
	 */
	getThumbnail(size: 'small' | 'medium'): string {
		if (!this.thumbs || this.thumbs.length === 0) {
			return this.cover
		}

		// Look for image with requested size in the filename
		const smallImage = this.thumbs.find((thumb) => thumb.includes(size))

		if (smallImage) {
			return smallImage
		}

		// Return empty string if nothing was found
		return ''
	}

	/**
	 * Get the smallest thumbnail image.
	 * Returns the image with "small" in the filename, or the cover if no thumbs exist.
	 */
	getSmallestThumbnail(): string {
		// Check for small thumbnail
		const smallImage = this.getThumbnail('small')

		if (smallImage) {
			return smallImage
		}

		// Check for medium thumbnail
		const mediumImage = this.getThumbnail('medium')

		if (mediumImage) {
			return mediumImage
		}

		// If no image was found, return cover as fallback
		return this.cover
	}

	/**
	 * Get the medium thumbnail image or cover if no medium thumb was found.
	 */
	getMediumImage(): string {
		// Check for medium thumbnail
		const mediumImage = this.getThumbnail('medium')

		if (mediumImage) {
			return mediumImage
		}

		// If no image was found, return cover as fallback
		return this.cover
	}
}

export class PlaylistAnalytics {
	public size: number
	public count: number
	public length: number
	public artists: string[]

	constructor(data: {
		size: number
		count: number
		length: number
		artists: string[]
	}) {
		this.size = data.size
		this.count = data.count
		this.length = data.length
		this.artists = data.artists
	}

	static create(data: Partial<PlaylistAnalytics>): PlaylistAnalytics {
		const defaultData = {
			size: 0,
			count: 0,
			length: 0,
			artists: [],
		}
		return new PlaylistAnalytics({
			...defaultData,
			...data,
		})
	}
}
